import {
  Component,
  EventEmitter,
  forwardRef,
  HostListener,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { ButtonStyle } from '@constants/button/1.0';
import { DomSanitizer } from '@angular/platform-browser';
import { readThumbnailFromInput } from '@helpers/file-reader';
import { CropImageDialogComponent } from '@components/crop-image-dialog/1.0/crop-image-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { take } from 'rxjs';
import {
  ImageUploadId,
  ImageUploadValidationFactory,
} from '@helpers/image-upload-validation-factory';
import { CatalogImagesService } from '@services/catalog-images/1.0/catalog-images.service';
import { UtilitiesService } from '@services/utilities/1.0/utilities.service';
import { OrderingChannels } from '@interfaces/aggregator/1.0';
import { LoadingColor } from '@constants/loading/1.0';
import { NgClass, NgIf } from '@angular/common';
import { LoadingComponent } from '../../loading/1.0/loading.component';
import { ButtonComponent } from '../../button/1.0/button.component';
import { TranslateModule } from '@ngx-translate/core';

@Component({
  selector: 'txp-file-upload',
  templateUrl: './file-upload.component.html',
  styleUrls: ['./file-upload.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => FileUploadComponent),
      multi: true,
    },
  ],
  standalone: true,
  imports: [NgClass, NgIf, LoadingComponent, ButtonComponent, TranslateModule],
})
export class FileUploadComponent implements ControlValueAccessor, OnChanges {
  @Input() label: string;
  @Input() accept: string;
  @Input() isPdf: boolean;
  @Input() showRequired: boolean;
  @Input() showThumbnail: boolean = true;
  @Input() showRemove: boolean = true;
  @Input() showCrop: boolean = false;
  @Input() showResize: boolean = false;
  @Input() autoResizeChannelId: string;
  @Input() forceAutoResize: boolean = false;
  @Input() disableFileUpload: boolean = false;
  @Output() setIsResizing: EventEmitter<boolean> = new EventEmitter<boolean>();
  @ViewChild('form', { static: true }) private form: HTMLFormElement;

  buttonStyle = ButtonStyle;
  loadingColor = LoadingColor;
  onChange: any;
  onTouched: any;
  thumbnail: any;
  resized = false;
  autoResized = false;
  originalFile: File;
  isLoadingThumbnail = false;
  orderingChannels = Object.values(OrderingChannels);

  private file: File;

  constructor(
    private sanitizer: DomSanitizer,
    private dialog: MatDialog,
    private catalogImagesService: CatalogImagesService,
    private utilitiesService: UtilitiesService,
  ) {}

  handleContainerClick(event: MouseEvent, fileInput: HTMLInputElement) {
    if (this.disableFileUpload) return;

    // Don't trigger file input if clicking on action buttons (crop, undo, delete)
    const target = event.target as HTMLElement;
    const fileActions = target.closest('.file-actions');
    const uploadLabel = target.closest('.image-upload');

    // Don't trigger if clicking on upload label (it already triggers file input naturally)
    if (uploadLabel) {
      return;
    }

    // Don't trigger if clicking on other action buttons in file-actions
    if (fileActions) {
      return;
    }

    fileInput?.click();
  }

  @HostListener('reset', ['$event'])
  handleReset(event) {
    // sometimes the parent form or sibling file-uploads were getting reset
    // this prevents that
    event.stopImmediatePropagation();
  }

  /**
   * Loads the selected file into the thumbnail variable for display
   *
   * @param event - file list
   */
  @HostListener('change', ['$event.target.files']) emitFiles(event: FileList) {
    const file = event && event.item(0);
    this.autoResized = false;
    this.onChange(file);
    this.file = file;
    this.setThumbnail();
    this.originalFile = file;
    this.resized = false;
  }

  get fileValue(): File {
    return this.file;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.autoResizeChannelId?.currentValue && this.file) {
      this.resizeImageFile(this.file);
    }
  }

  setThumbnail() {
    this.isLoadingThumbnail = true;
    readThumbnailFromInput(this.file, this.isPdf).subscribe({
      next: (value) => {
        const base64PdfContent = btoa(value);

        if (this.isPdf) {
          this.thumbnail = this.sanitizer.bypassSecurityTrustResourceUrl(
            `data:application/pdf;base64,${base64PdfContent}`,
          );
          this.isLoadingThumbnail = false;
        } else {
          this.revokeThumbnailUrl();
          this.thumbnail = value;
        }
      },
      error: () => {
        this.isLoadingThumbnail = false;
      },
    });
  }

  writeValue(value: any) {
    this.autoResized = false;
    // clear file input if value changed
    if (this.thumbnail) {
      this.removeUpload();
    }

    if (!value) {
      this.isLoadingThumbnail = false;
      return;
    }

    readThumbnailFromInput(value, this.isPdf).subscribe({
      next: (val) => {
        this.revokeThumbnailUrl();
        this.thumbnail = val;
        if (this.isPdf) {
          this.isLoadingThumbnail = false;
        }
      },
      error: () => {
        this.isLoadingThumbnail = false;
      },
    });

    if (this.forceAutoResize) {
      if (value instanceof File) {
        this.resizeImageFile(value);
      } else if (typeof value === 'string') {
        this.isLoadingThumbnail = true;
        this.catalogImagesService
          .fetchImageAsFile(value, 'imageFile')
          .pipe(take(1))
          .subscribe({
            next: (file) => {
              this.isLoadingThumbnail = false;
              this.resizeImageFile(file);
            },
            error: (err) => {
              this.isLoadingThumbnail = false;
            },
          });
      }
    }
  }

  registerOnChange(fn: any) {
    this.onChange = fn;
  }

  registerOnTouched(fn: any) {
    this.onTouched = fn;
  }

  removeUpload() {
    // have to reset the form to get the file input to fully reset
    // resetting the input value to undefined does not fully clear the
    // file input such that you are unable to select the last file you had selected
    // before clearing
    if (this.form) {
      this.form.nativeElement?.reset();
    }

    this.revokeThumbnailUrl();
    this.file = undefined;
    this.thumbnail = undefined;
  }

  private revokeThumbnailUrl() {
    if (typeof this.thumbnail === 'string' && this.thumbnail.startsWith('blob:')) {
      URL.revokeObjectURL(this.thumbnail);
    }
  }

  /**
   * Removes the file and fires onChange
   */
  handleDelete() {
    this.autoResized = false;
    this.removeUpload();
    this.onChange(undefined);
  }

  onOpenCropImage() {
    let imageType = 'jpeg';
    if (this.file) {
      imageType = this.file.type.split('/')[1];
      this.openCropImageDialog(this.file, imageType);
    } else {
      imageType = this.thumbnail.split('?')[0].split('/').pop().split('.').pop().toLowerCase();
      if (imageType === 'jpg') {
        imageType = 'jpeg';
      }
      this.isLoadingThumbnail = true;
      this.catalogImagesService
        .fetchImageAsFile(this.thumbnail, 'cropImageFile')
        .pipe(take(1))
        .subscribe({
          next: (value) => {
            this.isLoadingThumbnail = false;
            this.openCropImageDialog(value, imageType);
          },
          error: () => (this.isLoadingThumbnail = false),
        });
    }
  }

  private openCropImageDialog(imageFile: File, imageType: string) {
    const dialogRef = this.dialog.open(CropImageDialogComponent, {
      data: {
        imageFile,
        imageType,
        isResizeEnabled: this.showResize,
      },
    });

    dialogRef.afterClosed().subscribe(({ value, croppedImage }) => {
      if (value && croppedImage) {
        this.resized = true;
        const convertedFile = new File([croppedImage.blob], 'cropped-image', {
          type: croppedImage.blob.type,
        });
        this.onChange(convertedFile);
        this.file = convertedFile;

        readThumbnailFromInput(convertedFile, false).subscribe((value) => {
          this.revokeThumbnailUrl();
          this.thumbnail = value;
        });
      }
    });
  }

  resizeImageFile(file: File) {
    if (this.autoResizeChannelId) {
      this.setIsResizing.emit(true);
      this.utilitiesService
        .getFileDimension(file)
        .pipe(take(1))
        .subscribe({
          next: (dimensions) => {
            const targetDimensions = this.getResizeTargetDimensions(
              this.autoResizeChannelId,
              dimensions,
            );
            this.utilitiesService
              .resizeImage(file, targetDimensions.width, targetDimensions.height)
              .pipe(take(1))
              .subscribe({
                next: (value) => {
                  this.onChange(value);
                  this.file = value;
                  this.setThumbnail();
                  setTimeout(() => {
                    this.autoResized = true;
                    this.setIsResizing.emit(false);
                  }, 300);
                },
              });
          },
        });
    }
  }

  getResizeTargetDimensions(
    channelId: string,
    fileDimensions: { width: number; height: number },
  ): { width: number; height: number } | null {
    if (!channelId || !fileDimensions) return null;

    // Handle consumer-api subchannels
    const validationId = this.orderingChannels.includes(channelId as OrderingChannels)
      ? ImageUploadId.CONSUMER_API
      : (channelId as ImageUploadId);

    const allowedDimensions = ImageUploadValidationFactory.get(validationId).allowedImageDimensions;
    let { width: targetWidth, height: targetHeight } = fileDimensions;

    targetWidth = Math.max(Math.min(targetWidth, allowedDimensions[1]), allowedDimensions[0]);
    targetHeight = Math.max(Math.min(targetHeight, allowedDimensions[3]), allowedDimensions[2]);

    return { width: targetWidth, height: targetHeight };
  }

  undoResize() {
    this.resized = false;
    this.onChange(this.originalFile);
    this.file = this.originalFile;
    this.setThumbnail();
  }

  onImageLoad() {
    this.isLoadingThumbnail = false;
  }

  onImageError() {
    this.isLoadingThumbnail = false;
  }
}
